/**
 * The realtime channel: one WebSocket per tab to /api/ws (RULES §SEC-2 — vendor data never
 * reaches the browser directly). The server sends
 *   {t:'hello'}                      after auth
 *   {t:'watching', projects}         after a watch
 *   {t:'live', project_id, data}     vehicle state for watched projects
 *   {t:'event', type, subject_type, subject_id, project_id, title, severity, ts}
 * Event notices carry no payload; they invalidate TanStack Query keys (registered per
 * subject type by modules) so screens re-fetch through the permissioned API.
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { getAccessToken, refreshSession } from './api/auth';

export interface EventNotice {
  t: 'event';
  type: string;
  subject_type: string | null;
  subject_id: string | null;
  project_id: string | null;
  title: string | null;
  severity: string | null;
  ts: string | null;
}

export interface LiveMessage {
  t: 'live';
  project_id: string;
  data: unknown;
}

export type RealtimeStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'offline';

type Invalidator = (notice: EventNotice) => QueryKey[];
const invalidators = new Map<string, Invalidator[]>();

/** Modules declare which queries an event about `subjectType` makes stale. */
export function registerInvalidation(subjectType: string, fn: Invalidator): void {
  const list = invalidators.get(subjectType) ?? [];
  list.push(fn);
  invalidators.set(subjectType, list);
}

export function keysFor(notice: EventNotice): QueryKey[] {
  const fns = [...(invalidators.get(notice.subject_type ?? '') ?? []), ...(invalidators.get('*') ?? [])];
  return fns.flatMap((fn) => fn(notice));
}

const MAX_BACKOFF = 30_000;

export class RealtimeClient {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = true;
  private projects: string[] = [];
  private pending = new Map<string, QueryKey>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private liveSubs = new Set<(msg: LiveMessage) => void>();
  private eventSubs = new Set<(msg: EventNotice) => void>();
  private statusSubs = new Set<() => void>();
  status: RealtimeStatus = 'idle';

  constructor(private readonly queryClient: QueryClient) {}

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.ws?.close(1000);
    this.ws = null;
    this.setStatus('idle');
  }

  /** Receive live updates for these projects (the current scope). */
  watch(projects: string[]): void {
    this.projects = projects;
    this.send({ op: 'watch', projects });
  }

  onLive(fn: (msg: LiveMessage) => void): () => void {
    this.liveSubs.add(fn);
    return () => this.liveSubs.delete(fn);
  }

  onEvent(fn: (msg: EventNotice) => void): () => void {
    this.eventSubs.add(fn);
    return () => this.eventSubs.delete(fn);
  }

  subscribeStatus = (fn: () => void): (() => void) => {
    this.statusSubs.add(fn);
    return () => this.statusSubs.delete(fn);
  };

  getStatus = (): RealtimeStatus => this.status;

  private setStatus(s: RealtimeStatus): void {
    this.status = s;
    this.statusSubs.forEach((fn) => fn());
  }

  private send(msg: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    this.setStatus(this.attempts ? 'reconnecting' : 'connecting');
    let token = getAccessToken();
    if (!token && (await refreshSession())) token = getAccessToken();
    if (!token) return this.schedule();
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${window.location.host}/api/ws?access_token=${encodeURIComponent(token)}`);
    this.ws = ws;
    ws.onopen = () => {
      this.attempts = 0;
      this.setStatus('open');
      if (this.projects.length) this.send({ op: 'watch', projects: this.projects });
    };
    ws.onmessage = (e) => this.dispatch(e.data);
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.stopped) return;
      // 4401: the token was refused — refresh before trying again.
      if (e.code === 4401) void refreshSession();
      this.schedule();
    };
  }

  private schedule(): void {
    if (this.stopped) return;
    this.setStatus(navigator.onLine === false ? 'offline' : 'reconnecting');
    const delay = Math.min(MAX_BACKOFF, 1000 * 2 ** this.attempts) * (0.75 + Math.random() * 0.5);
    this.attempts += 1;
    this.timer = setTimeout(() => void this.connect(), delay);
  }

  private dispatch(raw: unknown): void {
    let msg: { t?: string };
    try {
      msg = JSON.parse(String(raw)) as { t?: string };
    } catch {
      return;
    }
    if (msg.t === 'live') this.liveSubs.forEach((fn) => fn(msg as LiveMessage));
    else if (msg.t === 'event') {
      const notice = msg as EventNotice;
      this.eventSubs.forEach((fn) => fn(notice));
      for (const key of keysFor(notice)) this.pending.set(JSON.stringify(key), key);
      // Coalesce bursts (a bulk edit emits many events) into one invalidation pass.
      if (!this.flushTimer) this.flushTimer = setTimeout(() => this.flush(), 250);
    }
  }

  private flush(): void {
    this.flushTimer = null;
    const keys = [...this.pending.values()];
    this.pending.clear();
    for (const queryKey of keys) void this.queryClient.invalidateQueries({ queryKey });
  }
}

let client: RealtimeClient | null = null;

export function getRealtime(queryClient: QueryClient): RealtimeClient {
  if (!client) client = new RealtimeClient(queryClient);
  return client;
}

export function useRealtimeStatus(rt: RealtimeClient): RealtimeStatus {
  return useSyncExternalStore(rt.subscribeStatus, rt.getStatus, rt.getStatus);
}

/** Subscribe to live vehicle frames while mounted. */
export function useLiveUpdates(rt: RealtimeClient | null, fn: (msg: LiveMessage) => void): void {
  useEffect(() => (rt ? rt.onLive(fn) : undefined), [rt, fn]);
}
